import {
  animate,
  ImageScaleView,
  ImageScaleViewOpts,
  ImageView,
} from '@play-co/timestep-core/ui';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { BoosterId } from '../defs/booster';
import i18n from 'src/lib/i18n/i18n';
import gsap from 'gsap';
import { matchTheme } from 'src/game/theme/match2/Match2Theme';

type Match2BoosterInfoOpts = Omit<
  ImageScaleViewOpts,
  'image' | 'width' | 'height'
>;

export class Match2BoosterInfo extends ImageScaleView {
  private icon: ImageScaleView;
  private title: LangBitmapFontTextView;
  private subtitle: LangBitmapFontTextView;
  private pointer: ImageView;
  private showing = false;

  constructor(opts: Match2BoosterInfoOpts) {
    const width = 600;
    const height = 200;

    super({
      width,
      height,
      image: matchTheme().getUIAsset('panel_boosters.png'),
      visible: false,
      ...opts,
    });

    this.icon = new ImageScaleView({
      superview: this,
      width: 90 * 1.5,
      height: 90 * 1.5,
      centerOnOrigin: true,
      x: 100,
      y: 100,
    });

    this.title = new LangBitmapFontTextView({
      parent: this,
      font: 'Title',
      color: '#FFFFFF',
      size: 40,
      localeText: () => 'TITLE',
      width: 400,
      height: 40,
      x: 200,
      y: 50,
      align: 'left',
    });

    this.subtitle = new LangBitmapFontTextView({
      parent: this,
      font: 'Body',
      color: '#000000',
      size: 26,
      localeText: () => 'SUBTITLE',
      width: 400,
      height: 26,
      x: 200,
      y: 100,
      align: 'left',
    });

    this.pointer = new ImageView({
      superview: opts.superview,
      image: 'assets/tutorial/hand/tutorial_hand.png',
      width: 96,
      height: 80,
      centerOnOrigin: true,
      scaleX: 0,
      scaleY: 0,
      visible: false,
      blockEvents: true,
    });
  }

  public showBooster(id: BoosterId, handPosition?: { x: number; y: number }) {
    const iconImage = matchTheme().getBoosterIcon(id);
    this.icon.updateOpts({ image: iconImage });
    this.title.updateOpts({
      localeText: () => i18n(`minigame.match2.boosterInfo.${id}.title`),
    });
    this.subtitle.updateOpts({
      localeText: () => i18n(`minigame.match2.boosterInfo.${id}.subtitle`),
    });
    this.animateShow();
    this.pointer.updateOpts({ visible: !!handPosition });

    if (handPosition) {
      const { x, y } = handPosition;
      this.pointer.updateOpts({
        x,
        y,
      });

      gsap.to(this.pointer.style, {
        duration: 0.375,
        offsetY: 7.5,
        repeat: -1,
        ease: 'quad.out',
        yoyo: true,
      });
    }
  }

  public hideBooster() {
    this.animateHide();
  }

  private async animateShow() {
    if (this.showing) return;
    this.showing = true;
    animate.getGroup('MatchBoosterInfo')?.clear();
    this.updateOpts({ visible: true });
    await animate(this, 'MatchBoosterInfo')
      .now({ offsetY: -200 })
      .then({ offsetY: 0 }, 400, animate.easeOutBack)
      .toPromise();

    await animate(this.pointer, 'MatchBoosterInfo')
      .now({ scaleX: 0, scaleY: 0 })
      .then({ scaleX: 1, scaleY: 1 }, 400, animate.easeOutBack)
      .toPromise();
  }

  private async animateHide() {
    if (!this.showing) return;
    this.showing = false;
    animate.getGroup('MatchBoosterInfo')?.clear();
    animate(this, 'MatchBoosterInfo')
      .now({ offsetY: 0 })
      .then({ offsetY: -200 }, 300, animate.easeInBack);

    await animate(this.pointer, 'MatchBoosterInfo')
      .now({ scaleX: 1, scaleY: 1 })
      .then({ scaleX: 0, scaleY: 0 }, 300, animate.easeInBack)
      .toPromise();

    gsap.killTweensOf(this.pointer.style);
    this.updateOpts({ visible: false });
  }
}
